import { useCallback } from "react";

import { cn } from "@/components/ui/utils";
import { ZIndex } from "@/constants";

/** Where each edge's strip sits - half over the card - and its line in it. */
const EDGES = {
  left: "left-[-6px] before:left-[5px]",
  right: "right-[-6px] before:right-[5px]",
  top: "top-[-6px] before:top-[5px]",
  bottom: "bottom-[-6px] before:bottom-[5px]",
};

type ReportsViewWidgetResizeHandleProps = {
  axis: "col" | "row";
  side: "left" | "right" | "top" | "bottom";
  step: number;
  /**
   * The ways this edge can go, as a cursor: one arrow when it can only grow
   * or only shrink, both when it can do either. An edge that can do neither
   * has no handle.
   */
  cursor: string | null;
  onResize: (delta: number) => void;
  onResizeEnd?: () => void;
};

export function ReportsViewWidgetResizeHandle({
  axis,
  side,
  step,
  cursor,
  onResize,
  onResizeEnd,
}: ReportsViewWidgetResizeHandleProps) {
  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (step <= 0) return;

      event.preventDefault();
      event.stopPropagation();

      const target = event.currentTarget;
      // Capture is how a drag hears the pointer once it has left a twelve
      // pixel handle - including the release, wherever that happens, even
      // over the browser's own chrome. It is asked for first because of
      // that, and the window is listened to only when it is refused: a
      // pointer the page never saw, or one released as it was pressed. A
      // drag that heard neither would never end, and the layout it was
      // previewing would sit over the page until it was reloaded.
      let captured = false;
      try {
        target.setPointerCapture(event.pointerId);
        captured = true;
      } catch {
        // Nothing to do: the window will hear it instead.
      }
      const heardBy: EventTarget = captured ? target : window;

      const start = axis === "col" ? event.clientX : event.clientY;
      let appliedSteps = 0;

      const onPointerMove = (moveEvent: PointerEvent) => {
        const deltaPrimary =
          axis === "col"
            ? moveEvent.clientX - start
            : moveEvent.clientY - start;
        // Every edge follows the pointer, the top one included: a step is
        // which way the pointer went, and the layout knows what moving that
        // edge that way means. The top edge used to be handed the opposite,
        // so dragging it up - which is a widget growing upwards - was read as
        // the widget getting shorter.
        const steps = Math.round(deltaPrimary / step);
        const increment = steps - appliedSteps;
        if (increment !== 0) {
          onResize(increment);
          appliedSteps = steps;
        }
      };

      // A pointer taken away from the page - by another window, by a touch
      // the browser decides was a scroll - is a drag that is over.
      const onPointerUp = (upEvent: PointerEvent) => {
        try {
          target.releasePointerCapture(upEvent.pointerId);
        } catch {
          // It was never captured; there is nothing to release.
        }
        heardBy.removeEventListener("pointermove", onPointerMove);
        heardBy.removeEventListener("pointerup", onPointerUp);
        heardBy.removeEventListener("pointercancel", onPointerUp);
        window.removeEventListener("pointercancel", onPointerUp);
        onResizeEnd?.();
      };

      heardBy.addEventListener("pointermove", onPointerMove);
      heardBy.addEventListener("pointerup", onPointerUp);
      heardBy.addEventListener("pointercancel", onPointerUp);
      window.addEventListener("pointercancel", onPointerUp);
    },
    [axis, onResize, onResizeEnd, side, step],
  );

  const isCol = axis === "col";

  if (!cursor) return null;

  return (
    <div
      onPointerDown={handlePointerDown}
      className={cn(
        "absolute pointer-events-auto",
        isCol ? "top-0 bottom-0 w-3" : "left-0 right-0 h-3",
        EDGES[side],
        // A mouse's drag only: the strip along a card's edge would stop a
        // finger scrolling the report wherever it started on it.
        "pointer-fine:touch-none",
        "before:content-[''] before:absolute before:rounded-sm before:bg-border before:opacity-0 before:[transition:opacity_0.15s_ease] hover:before:opacity-100",
        isCol
          ? "before:top-[20%] before:bottom-[20%] before:w-0.5"
          : "before:left-[20%] before:right-[20%] before:h-0.5",
      )}
      style={{ cursor, zIndex: ZIndex.GRID.COLUMN_RESIZE_HOVER }}
    />
  );
}
